/**
 * RouteListView Component
 *
 * A list view for displaying routes at a gym.
 * Shows scrollable route rows with completion status.
 * Sort control is handled by parent via sortOption prop.
 *
 * @example
 * ```tsx
 * <RouteListView
 *   routes={routes}
 *   colorGrades={gym.colorGrades}
 *   sentRouteIds={sentRouteIds}
 *   sortOption="default"
 *   onRouteSelect={setSelectedRoute}
 * />
 * ```
 */

import { useMemo, useEffect, useRef, type CSSProperties } from 'react';
import { RouteRow } from './RouteRow';
import type { Route, ColorGrade, PrimaryIdentificationMethod } from '@flash/shared';

/**
 * Sort options for the route list
 */
export type SortOption = 'default' | 'grade-asc' | 'grade-desc' | 'age-new' | 'age-old' | 'ascents';

/**
 * Props for RouteListView component
 */
export interface RouteListViewProps {
  /** Routes to display */
  routes: Route[];
  /** Gym's color grades for hold colors (or all colors when linked) */
  colorGrades: ColorGrade[];
  /** Gym's tag color grades (only used when colors are unlinked) */
  tagColorGrades?: ColorGrade[] | undefined;
  /** Primary identification method from gym settings */
  primaryMethod?: PrimaryIdentificationMethod | undefined;
  /** Set of route IDs the user has sent */
  sentRouteIds: Set<string>;
  /** Called when a route is selected */
  onRouteSelect: (route: Route) => void;
  /** Selected route ID for highlighting */
  selectedRouteId?: string;
  /** Current sort option (controlled by parent) */
  sortOption?: SortOption;
}

/**
 * Sort option labels for dropdown
 */
export const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'default', label: 'Default' },
  { value: 'grade-asc', label: 'Grade (easy first)' },
  { value: 'grade-desc', label: 'Grade (hard first)' },
  { value: 'age-new', label: 'Newest first' },
  { value: 'age-old', label: 'Oldest first' },
  { value: 'ascents', label: 'Most ascents' },
];

/**
 * Grade order for sorting (V0 = 0, V17 = 17)
 */
const getGradeOrder = (grade: string): number => {
  const match = grade.match(/V(\d+)/);
  const gradeNum = match?.[1];
  return gradeNum ? parseInt(gradeNum, 10) : 0;
};

/**
 * Get timestamp from date-like value
 */
const getTimestamp = (date: Date | { seconds: number }): number => {
  if (date instanceof Date) return date.getTime();
  return (date as { seconds: number }).seconds * 1000;
};

/**
 * RouteListView Component
 */
export const RouteListView = ({
  routes,
  colorGrades,
  tagColorGrades,
  primaryMethod = 'holdColors',
  sentRouteIds,
  onRouteSelect,
  selectedRouteId,
  sortOption = 'default',
}: RouteListViewProps) => {
  // Refs for auto-scroll
  const listRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Auto-scroll to selected route when selectedRouteId changes
  useEffect(() => {
    if (!selectedRouteId) return;

    const rowElement = rowRefs.current.get(selectedRouteId);
    if (rowElement && listRef.current) {
      // Scroll the row into view with smooth animation
      rowElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [selectedRouteId]);

  // Sort routes based on selected option
  const sortedRoutes = useMemo(() => {
    const sorted = [...routes];

    // Helper to get grade order, putting null grades at the end
    const getSafeGradeOrder = (grade: string | null): number => {
      if (!grade) return 999; // Put routes without grades at the end
      return getGradeOrder(grade);
    };

    switch (sortOption) {
      case 'grade-asc':
        sorted.sort(
          (a, b) => getSafeGradeOrder(a.officialGrade) - getSafeGradeOrder(b.officialGrade)
        );
        break;
      case 'grade-desc':
        sorted.sort(
          (a, b) => getSafeGradeOrder(b.officialGrade) - getSafeGradeOrder(a.officialGrade)
        );
        break;
      case 'age-new':
        sorted.sort((a, b) => getTimestamp(b.setDate) - getTimestamp(a.setDate));
        break;
      case 'age-old':
        sorted.sort((a, b) => getTimestamp(a.setDate) - getTimestamp(b.setDate));
        break;
      case 'ascents':
        sorted.sort((a, b) => b.totalSends - a.totalSends);
        break;
      case 'default':
      default:
        // Default: newest first, then by grade
        sorted.sort((a, b) => {
          const timeDiff = getTimestamp(b.setDate) - getTimestamp(a.setDate);
          if (Math.abs(timeDiff) > 7 * 24 * 60 * 60 * 1000) return timeDiff; // More than a week apart
          return getSafeGradeOrder(a.officialGrade) - getSafeGradeOrder(b.officialGrade);
        });
        break;
    }

    return sorted;
  }, [routes, sortOption]);

  return (
    <div ref={listRef} style={containerStyle}>
      {sortedRoutes.length === 0 ? (
        <p style={emptyStyle}>No routes to display</p>
      ) : (
        sortedRoutes.map((route) => (
          <div
            key={route.id}
            ref={(el) => {
              if (el) rowRefs.current.set(route.id, el);
            }}
          >
            <RouteRow
              route={route}
              colorGrades={colorGrades}
              tagColorGrades={tagColorGrades}
              primaryMethod={primaryMethod}
              isSent={sentRouteIds.has(route.id)}
              isSelected={selectedRouteId === route.id}
              onClick={() => onRouteSelect(route)}
            />
          </div>
        ))
      )}
    </div>
  );
};

// Styles
const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
};

const emptyStyle: CSSProperties = {
  padding: '40px 16px',
  textAlign: 'center',
  color: 'var(--color-text-secondary, #6b7280)',
};

export default RouteListView;
